import React, { useState, useEffect, useRef } from "react";
import { getDetalleCurso } from "./service/serviceModalDetalleCurso";
import CursoModulos from "./components/CursoModulos";
import ModalCertificado from "../../../ModalCertificado";
import useTokenValidation from "../../../../../../../../hooks/useTokenValidation";
import { Oval } from "react-loader-spinner";

export default function ModalDetalleCurso({
  onClose = () => {},
  courseId = null,
  homeContainerRef, // Ref del contenedor padre (por ejemplo, "perfil-info-2-mobile")
}) {
  // Estados para datos, carga y error
  const [cursoDetalle, setCursoDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { tokenEntity } = useTokenValidation();

  // Estados para la información del curso y certificado
  const [certificadoActivo, setCertificadoActivo] = useState(false);
  const [datosCertificado, setDatosCertificado] = useState(null);
  const [infoGeneral, setInfoGeneral] = useState(null);
  const [infoEspecificaCurso, setInfoEspecificaCurso] = useState(null);

  // Estado para controlar qué scroll tiene el control: "parent" o "child"
  const [activeScroll, setActiveScroll] = useState("parent");

  // Ref para el contenedor interno del modal (scroll "hijo" – con clase "curso-ventana-emergente-info")
  const modalScrollRef = useRef(null);

  // Valor mínimo fijo para el threshold (en píxeles)
  const FIXED_THRESHOLD = 5;

  const handleCertificadoClick = (infoGeneralCurso) => {
    console.log(infoGeneralCurso);
    setCertificadoActivo(true);
    setDatosCertificado(infoGeneralCurso);
  };

  // --- Fetch de datos del curso ---
  useEffect(() => {
    if (!tokenEntity.isValidando && courseId) {
      getDetalleCurso(tokenEntity.token, courseId)
        .then((response) => {
          if (response.ok) return response.json();
          switch (response.status) {
            case 400:
              throw new Error("ERR_VALIDATION");
            case 401:
              throw new Error("ERR_UNAUTHORIZED");
            case 500:
              throw new Error("ERR_SERVER");
            default:
              throw new Error("ERR_UNKNOWN");
          }
        })
        .then((result) => {
          if (result && result.detail.acción === "DETALLE_CURSO_OBTENIDO") {
            console.log("Detalle del curso:", result.detail.datos);
            setCursoDetalle(result.detail.datos);
            setInfoGeneral(result.detail.datos.info_general_curso);
            setInfoEspecificaCurso(result.detail.datos.info_especifica_curso);
          }
        })
        .catch((error) => {
          console.log("Error de red", error);
          setError("Error en la conexión con el servidor");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [tokenEntity.isValidando, courseId]);

  // --- Lógica para transferir el control del scroll del padre al hijo ---
  useEffect(() => {
    if (homeContainerRef && homeContainerRef.current) {
      const parent = homeContainerRef.current;
      const maxParentScroll = parent.scrollHeight - parent.clientHeight;
      // Umbral calculado responsivamente: 5% del scroll máximo o FIXED_THRESHOLD, lo que sea mayor
      const calculatedThreshold = Math.max(FIXED_THRESHOLD, maxParentScroll * 0.05);

      const onParentScroll = () => {
        if (activeScroll !== "parent") return;
        console.log("Parent scrollTop:", parent.scrollTop, "Max:", maxParentScroll, "Threshold:", calculatedThreshold);
        if (parent.scrollTop >= maxParentScroll - calculatedThreshold) {
          // Transferimos el control al hijo (no se modifica el overflow del padre)
          setActiveScroll("child");
          console.log("Transfer control to child; parent's scrollTop:", parent.scrollTop);
        }
      };

      parent.addEventListener("scroll", onParentScroll);
      return () => {
        parent.removeEventListener("scroll", onParentScroll);
      };
    }
  }, [homeContainerRef, activeScroll]);

  // --- Control del overflow del contenedor interno del modal según activeScroll ---
  useEffect(() => {
    if (modalScrollRef && modalScrollRef.current) {
      const modal = modalScrollRef.current;
      // Si existe homeContainerRef y éste contiene al modal:
      if (
        homeContainerRef &&
        homeContainerRef.current &&
        homeContainerRef.current.contains(modal)
      ) {
        modal.style.overflowY = activeScroll === "child" ? "auto" : "hidden";
      } else {
        // Si el modal no está dentro de homeContainerRef, forzamos scroll "auto"
        modal.style.overflowY = "auto";
      }
    }
  }, [activeScroll, homeContainerRef]);

  // --- Listener para eventos "wheel" en el modal para devolver el control al padre ---
  useEffect(() => {
    if (modalScrollRef && modalScrollRef.current) {
      const modal = modalScrollRef.current;
      const onChildWheel = (e) => {
        if (activeScroll !== "child") return;
        if (e.deltaY < 0 && modal.scrollTop <= FIXED_THRESHOLD) {
          e.preventDefault();
          setActiveScroll("parent");
          console.log("Return control to parent (wheel); modal.scrollTop:", modal.scrollTop);
        }
      };
      modal.addEventListener("wheel", onChildWheel, { passive: false });
      return () => {
        modal.removeEventListener("wheel", onChildWheel);
      };
    }
  }, [homeContainerRef, activeScroll]);

  // --- Listener para eventos táctiles en el modal para devolver el control al padre ---
  useEffect(() => {
    if (
      homeContainerRef &&
      homeContainerRef.current &&
      modalScrollRef &&
      modalScrollRef.current
    ) {
      const modal = modalScrollRef.current;
      let touchStartY = 0;

      const onTouchStart = (e) => {
        if (activeScroll !== "child") return;
        touchStartY = e.touches[0].clientY;
      };

      const onTouchMove = (e) => {
        if (activeScroll !== "child") return;
        const currentY = e.touches[0].clientY;
        const deltaY = currentY - touchStartY;
        if (deltaY > 0 && modal.scrollTop <= FIXED_THRESHOLD) {
          e.preventDefault();
          setActiveScroll("parent");
          console.log("Touch: Return control to parent; modal.scrollTop:", modal.scrollTop);
        }
      };

      modal.addEventListener("touchstart", onTouchStart, { passive: false });
      modal.addEventListener("touchmove", onTouchMove, { passive: false });
      return () => {
        modal.removeEventListener("touchstart", onTouchStart);
        modal.removeEventListener("touchmove", onTouchMove);
      };
    }
  }, [homeContainerRef, activeScroll]);

  if (loading) {
    return (
      <div data-figma-id="1623:15207" className="curso-ventana-emergente">
        <div data-figma-id="1623:15208" className="curso-ventana-emergente-header">
          cargando ....
        </div>
        <div
          data-figma-id="1623:15216"
          className="curso-ventana-emergente-info"
          ref={modalScrollRef}
        >
          <Oval
            visible={true}
            height="50"
            width="50"
            color="#fff"
            secondaryColor="#f5f5f5"
            ariaLabel="oval-loading"
          />
        </div>
      </div>
    );
  }
  if (error) {
    return <p>{error}</p>;
  }

  return (
    <div data-figma-id="1623:15207" className="curso-ventana-emergente">
      <div
        data-figma-id="1623:15208"
        className="curso-ventana-emergente-header"
      >
        <i className="fi fi-br-cross x-1-1" onClick={onClose}></i>
        <div
          data-figma-id="1623:15210"
          className="curso-ventana-emergente-titulos"
        >
          <p
            data-figma-id="1623:15211"
            className="curso-ventana-emergente-titulos-1"
          >
            {infoGeneral.nombre_curso}
          </p>
          <p
            data-figma-id="1623:15212"
            className="curso-ventana-emergente-titulos-2"
          >
            {infoGeneral.grupo}
          </p>
          {/* Icono de estado del curso 
          <div
            data-figma-id="1623:15213"
            className="curso-ventana-emergente-promedio"
          >
            <p
              data-figma-id="1623:15214"
              className="curso-ventana-emergente-promedio-1"
            >
              {infoGeneral.calificacion_curso}
            </p>
            <p
              data-figma-id="1623:15215"
              className="curso-ventana-emergente-promedio-2"
            >
              promedio
            </p>
          </div>
          */}
        </div>
      </div>
      <div
        data-figma-id="1623:15216"
        className="curso-ventana-emergente-info"
        ref={modalScrollRef}
      >
        <div data-figma-id="1623:15217" className="frame-290180">
          <div
            data-figma-id="1623:15218"
            className="curso-ventana-emergente-info-tarjetas"
          >
            <div data-figma-id="1623:15219" className="tarjeta-modulo">
              <i className="fi fi-tr-book-alt libro-alternativo-3"></i>
              <p data-figma-id="1623:15221" className="tarjeta-modulo-texto-1">
                Secciones
              </p>
              <p data-figma-id="1623:15222" className="tarjeta-modulo-texto-2">
                {infoGeneral.secciones_completadas}
              </p>
            </div>
            <div data-figma-id="1623:15223" className="tarjeta-lecciones">
              <i class="fi fi-ts-memo-circle-check libro-alternativo-3"></i>
              <p
                data-figma-id="1623:15225"
                className="tarjeta-lecciones-texto-1"
              >
                Lecciones
              </p>
              <p
                data-figma-id="1623:15226"
                className="tarjeta-lecciones-texto-2"
              >
                {infoGeneral.lecciones_completadas}
              </p>
            </div>
            {/* Tarjeta de certificado con onClick 
            {infoGeneral.certificado === true ? (
              <div
                data-figma-id="1623:15227"
                className="tarjeta-certificado-activado"
                onClick={() => handleCertificadoClick(infoGeneral)}
              >
                <i className="fi fi-rr-diploma diploma-2-1-mobile-activo"></i>
                <p
                  data-figma-id="1623:15228"
                  className="tarjeta-certificado-texto-activado"
                >
                  Certificado
                </p>
                <i className="fi fi-rr-diploma diploma-2-1-activo"></i>
              </div>
            ) : (
              <div
                data-figma-id="1623:15227"
                className="tarjeta-certificado"
                onClick={() => handleCertificadoClick(infoGeneral)}
              >
                <i className="fi fi-rr-diploma diploma-2-1-mobile"></i>
                <p
                  data-figma-id="1623:15228"
                  className="tarjeta-certificado-texto"
                >
                  Certificado
                </p>
                <i className="fi fi-rr-diploma diploma-2-1"></i>
              </div>
            )}
              */}

            <div
              data-figma-id="1623:15230"
              className="estado-en-proceso-mobile"
            >
              <p data-figma-id="1623:15231" className="estado-en-proceso-texto">
                En proceso
              </p>
            </div>
          </div>
          <div data-figma-id="1623:15230" className="estado-en-proceso">
            <p data-figma-id="1623:15231" className="estado-en-proceso-texto">
              En proceso
            </p>
          </div>
        </div>

        <CursoModulos
          infoEspecificaCurso={infoEspecificaCurso}
          cursoID={courseId}
        />
        {/* La clase del contenedor cambia según el estado certificadoActivo */}
        <div
          className={
            certificadoActivo
              ? "box-modal-certificado-active"
              : "box-modal-certificado"
          }
        >
          <ModalCertificado
            certificado={datosCertificado?.certificado}
            datosCurso={infoGeneral}
            onClose={onClose}
          />
        </div>
      </div>
    </div>
  );
}
